import * as React from 'react';
import {ListGroup, ListGroupItem, LineBreak} from '@openfin/desktop-ui';

import {SearchResult, SearchEngineError, SearchResponse, EngineAction} from '../../../model/Search';
import {AccountInfo} from '../../../store/partials/Authentication';
import {SearchEngine} from '../../../types/commons';
import {ClassNameBuilder} from '../../utils/ClassNameBuilder';

import {EngineSearchResultEntry} from './EngineSearchResultEntry';
import * as Styles from './EngineSearchResults.module.scss';
import {NoResults, SearchError} from './SearchMessage';
import {SearchLoading} from './SearchLoading';

interface Props {
    search: string;
    searchEngine: SearchEngine;
    account: AccountInfo;
    keyboardEventSource: React.RefObject<HTMLElement>;
}

interface ResultsEngineSearchState {
    type: 'results';
    results: SearchResult[];
}

interface InProgressEngineSearchState {
    type: 'in-progress';
}

interface ErrorEngineSearchState {
    type: 'error';
}

interface EmptyEngineSearchState {
    type: 'empty';
}

type EngineSearchState = ResultsEngineSearchState | InProgressEngineSearchState | ErrorEngineSearchState | EmptyEngineSearchState;

let lastRequest: AbortController | null = null;

const requiredResultFields: (keyof SearchResult)[] = ['title', 'description', 'action'];
const allowedActionTypes: EngineAction['type'][] = ['application', 'browser', 'intent'];
function isValidResponse(results: SearchResponse | null): results is SearchResponse {
    if (results !== null) {
        return Array.isArray(results) && results.every((result) => {
            const type = typeof result;
            const keys = Object.keys(result || {});
            if (type !== 'object') {
                console.error('Search results must be an array of objects', results);
            } else if (requiredResultFields.some((requiredField) => !keys.includes(requiredField))) {
                console.error(`Results must contain ${requiredResultFields.join()}:`, result);
            } else if (!allowedActionTypes.includes(result.action?.type)) {
                console.error(`Result action not one of ${allowedActionTypes.join()}`, result);
            } else {
                return true;
            }

            return false;
        });
    } else {
        return false;
    }
}

export const EngineSearchResults: React.FC<Props> = (props) => {
    const {searchEngine, search, keyboardEventSource, account} = props;
    const [engineSearchState, setEngineSearchState] = React.useState<EngineSearchState>({type: 'empty'});

    /**
     * Send request to given search engine's endpoint. Will parse and return the results received from the server.
     */
    async function fetchEngineResults(): Promise<SearchResponse | null> {
        let url = searchEngine.url;
        url += `${url.includes('?') ? '&' : '?'}q=${encodeURIComponent(search)}`;
        if (account && account.email) {
            url += `&u=${encodeURIComponent(account.email)}`;
        }

        if (lastRequest) {
            lastRequest.abort();
            lastRequest = null;
        }

        let response: Response;
        try {
            lastRequest = new AbortController();
            response = await fetch(url, {signal: lastRequest.signal});
        } catch (e) {
            if (e instanceof DOMException && e.code === DOMException.ABORT_ERR) {
                // Request was aborted because there was a more recent request
                return null;
            } else {
                throw new SearchEngineError('Error when querying search engine', e, url);
            }
        }

        if (response.status >= 400) {
            throw new SearchEngineError(`Search engine returned ${response.status} (${response.statusText})`, undefined, url);
        }

        try {
            return await response.json();
        } catch (e) {
            throw new SearchEngineError('Search engine returned malformed response', e, url);
        }
    }

    React.useEffect(() => {
        const query = search.trim();
        // Cancel async task on unmount
        let cancelled = false;
        if (query.length > 0) {
            setEngineSearchState({type: 'in-progress'});
            fetchEngineResults().then((results) => {
                if (!cancelled) {
                    if (isValidResponse(results)) {
                        setEngineSearchState({type: 'results', results});
                    } else {
                        setEngineSearchState({type: 'error'});
                    }
                }
            }, () => {
                if (!cancelled) {
                    setEngineSearchState({type: 'error'});
                }
            });
        } else {
            setEngineSearchState({type: 'empty'});
        }
        return () => {
            cancelled = true;
        };
    }, [search]);

    let sectionInner: React.ReactNode | null = null;
    let message: React.ReactNode | null = null;

    if (engineSearchState.type === 'results') {
        if (engineSearchState.results.length > 0) {
            sectionInner = (
                <ListGroup className={Styles['list']} changeResetsActiveItem firstItemActive border={false} focusElement={keyboardEventSource}>
                    {
                        engineSearchState.results.map((searchResult: SearchResult, index: number) => {
                            return (
                                <ListGroupItem key={index} item={searchResult} itemActiveBackground={false}>
                                    <EngineSearchResultEntry item={searchResult} />
                                </ListGroupItem>
                            );
                        })
                    }
                </ListGroup>
            );
        } else {
            message = <NoResults />;
        }
    } else if (engineSearchState.type === 'error') {
        message = <SearchError engine={searchEngine.title} />;
    } else if (engineSearchState.type === 'in-progress') {
        message = <SearchLoading />;
    }

    const containerClass: string = new ClassNameBuilder(Styles, 'engine-search-results').toString();

    return (
        <div className={containerClass}>
            <LineBreak>Search Results</LineBreak>
            {sectionInner}
            {message}
        </div>
    );
};
